import { forwardRef, useEffect, useState } from 'react';
import { InputBase } from '@mui/material';
import { useInterstateTheme } from '../../InterstateThemeProvider';
import { resolveInputFontSize } from '../../TextInput/TextInputStyles';
import { jsx as _jsx } from "react/jsx-runtime";
const resolveInputStyle = (theme, size, multiple, width, disabled) => ({
  overFlow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: theme.tokens.SemTypeFontBody,
  fontSize: resolveInputFontSize(theme, size),
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '100%',
  width: '0px',
  minWidth: '0px',
  flexGrow: 1,
  '& .MuiInputBase-input': {
    padding: '0',
    fontSize: resolveInputFontSize(theme, size),
    lineHeight: '100%',
    height: 'fit-content',
    cursor: !disabled ? 'default' : 'not-allowed'
  }
});
/**
 * ComboBoxInput is a component that represents the input field of a combo box.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.placeholder - The placeholder text to display in the input field
 * @param {string} props.size - The size of the input field
 * @param {boolean} props.disabled - Specifies if the input field is disabled
 * @param {Function} props.onFocus - Event handler for when the input field receives focus
 * @param {Function} props.onInputChange - Event handler for when the input value changes
 * @param {Function} props.onInputClick - Event handler for when the input field is clicked
 * @param {string} props.value - The current value of the input field
 * @param {boolean} props.multiple - Specifies if the combo box supports multiple selections
 *
 * @example
 * <ComboBoxInput
 *   placeholder="Select an option"
 *   size="medium"
 *   disabled={false}
 *   onFocus={() => handleFocus()}
 *   onInputChange={(e) => handleInputChange(e)}
 *   onInputClick={() => handleInputClick()}
 *   value="Option 1"
 *   multiple={false}
 * />
 */
export const ComboBoxInput = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    placeholder,
    size,
    disabled,
    onFocus,
    onInputChange,
    onInputClick,
    value,
    multiple,
    width,
    id,
    ...rest
  } = props;
  const theme = useInterstateTheme();
  const [_inputValue, setInputValue] = useState('');
  const inputStyle = resolveInputStyle(theme, size, multiple, width, disabled);
  const handleOnChange = e => {
    setInputValue(e.target.value);
    onInputChange(e);
  };
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);
  return /*#__PURE__*/_jsx(InputBase, {
    className: 'ids-combo-box-input',
    placeholder: placeholder,
    sx: inputStyle,
    disabled: disabled,
    inputRef: ref,
    onFocus: onFocus,
    onChange: handleOnChange,
    onClick: e => {
      e.stopPropagation();
      onInputClick(e);
    },
    value: _inputValue,
    id: id,
    inputProps: {
      'data-testid': rest['data-testid'] || 'ids-combo-box-input'
    }
  });
});
ComboBoxInput.displayName = 'ComboBoxInput';